import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { authApi } from '../api';

const emptyForm = { currentPassword: '', newPassword: '', confirm: '' };

export default function ForceChangePassword() {
  const { logout } = useAuth();
  const [form, setForm]   = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // If the browser restores this page from bfcache (e.g. pressing Back after
  // the password change already logged the user out), force a fresh load so
  // routing re-evaluates instead of showing a stale, no-longer-valid form.
  useEffect(() => {
    const onPageShow = (e) => { if (e.persisted) window.location.reload(); };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  const validate = (f) => {
    const errs = {};
    if (!f.currentPassword) errs.currentPassword = 'Temporary password is required';
    if (!f.newPassword) errs.newPassword = 'New password is required';
    else if (f.newPassword.length < 6) errs.newPassword = 'New password must be at least 6 characters';
    if (!f.confirm) errs.confirm = 'Please confirm your new password';
    else if (f.newPassword && f.newPassword !== f.confirm) errs.confirm = 'Passwords do not match';
    if (f.currentPassword && f.newPassword && f.currentPassword === f.newPassword)
      errs.newPassword = 'New password must differ from the temporary password';
    return errs;
  };

  const handleChange = (field) => (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) setErrors((prev) => { const n = { ...prev }; delete n[field]; return n; });
    if (submitError) setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    setSubmitError('');
    try {
      await authApi.changePassword({ currentPassword: form.currentPassword, newPassword: form.newPassword });
      // Force a fresh login with the new password rather than continuing the
      // old session — also clears the token/cookie so Back can't return to
      // an authenticated state that would re-render this page.
      setDone(true);
      setTimeout(() => logout(), 1200);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to change password';
      if (msg.toLowerCase().includes('current')) setErrors({ currentPassword: 'Temporary password is incorrect' });
      else setSubmitError(msg);
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-card-lg border border-slate-100 p-8 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-6 h-6 text-emerald-600">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-slate-900">Password updated</h2>
            <p className="text-slate-400 text-sm mt-1">Signing you out — please log in with your new password.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-card-lg border border-slate-100 p-8">
          <div className="mb-7">
            <h2 className="text-2xl font-bold text-slate-900">Set a new password</h2>
            <p className="text-slate-400 text-sm mt-1">
              For security, you must change your temporary password before continuing.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="label">Temporary Password</label>
              <input
                className="input"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={form.currentPassword}
                onChange={handleChange('currentPassword')}
              />
              {errors.currentPassword && <p className="text-red-600 text-xs mt-1">{errors.currentPassword}</p>}
            </div>

            <div>
              <label className="label">New Password</label>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                value={form.newPassword}
                onChange={handleChange('newPassword')}
              />
              {errors.newPassword && <p className="text-red-600 text-xs mt-1">{errors.newPassword}</p>}
            </div>

            <div>
              <label className="label">Confirm New Password</label>
              <input
                className="input"
                type="password"
                autoComplete="new-password"
                value={form.confirm}
                onChange={handleChange('confirm')}
              />
              {errors.confirm && <p className="text-red-600 text-xs mt-1">{errors.confirm}</p>}
            </div>

            {submitError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm p-3 rounded-lg">
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 flex-shrink-0">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                </svg>
                {submitError}
              </div>
            )}

            <button type="submit" disabled={loading} className="btn-primary w-full py-3 text-base mt-2">
              {loading ? 'Updating…' : 'Update Password'}
            </button>
          </form>

          <button
            type="button"
            onClick={logout}
            className="text-center text-xs text-slate-400 hover:text-slate-600 mt-6 w-full"
          >
            Log out instead
          </button>
        </div>
      </div>
    </div>
  );
}
